import { useEffect, useState } from "react";
import { Button, Flex, Modal, message, Table, Input, Spin } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  postDeviceType,
  putDeviceType,
  deleteDeviceType,
  fetchDeviceType,
} from "../Service/deviceType";
import DeviceTypeForm from "./deviceTypeForm";

let searchTimeout = null;

const DeviceType = () => {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [search, setSearch] = useState(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [sortField, setSortField] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("descend");

  // ================= FETCH =================
  const fetchData = async (
    pageNum = page,
    pageSize = size,
    searchText = search,
    field = sortField,
    order = sortOrder
  ) => {
    setLoading(true);
    try {
      const res = await fetchDeviceType({
        page: pageNum - 1,
        size: pageSize,
        search: searchText || "",
        sort: field,
        desc: order === "descend",
      });

      setDeviceTypes(res.data?.content || []);
      setTotal(res.data?.totalElements || 0);
      setPage(pageNum);
      setSize(pageSize);
      setSortField(field);
      setSortOrder(order);
    } catch {
      message.error("Network error while fetching device types");
    } finally {
      setLoading(false);
    }
  };

  // ================= SAVE =================
  const saveData = async (data) => {
    setLoading(true);
    try {
      if (editData) {
        await putDeviceType(data, editData.uuid);
      } else {
        await postDeviceType(data);
      }
      messageApi.success(
        `Device type ${editData ? "updated" : "added"} successfully`
      );
      handleCancel();
      fetchData();
    } catch {
      messageApi.error("Error while saving device type");
    } finally {
      setLoading(false);
    }
  };

  // ================= DELETE =================
  const deleteData = async (id) => {
    try {
      await deleteDeviceType(id);
      messageApi.success("Device type deleted successfully");
      fetchData();
    } catch {
      messageApi.error("Error while deleting device type");
    }
  };

  const handleCancel = () => {
    fetchData();
    setIsModalOpen(false);
    setEditData(null);
  };

  // ================= SEARCH (DEBOUNCE) =================
  useEffect(() => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      fetchData(1, size, search);
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [search]);

  // ================= INIT =================
  useEffect(() => {
    fetchData();
  }, []);

  // ================= COLUMNS =================
  const columns = [
    {
      title: "Device Type",
      dataIndex: "deviceType",
      key: "deviceType",
      sorter: true,
      sortOrder: sortField === "deviceType" ? sortOrder : null,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <>
          <EditOutlined
            style={{ marginRight: 12, cursor: "pointer" }}
            onClick={() => {
              setEditData(record);
              setIsModalOpen(true);
            }}
          />
          <DeleteOutlined
            style={{ cursor: "pointer" }}
            onClick={() => deleteData(record.uuid)}
          />
        </>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ width: "100%" }}>
        {contextHolder}

        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <div style={{ fontSize: "1rem", fontWeight: 700 }}>Device Type</div>

          <Flex gap={8}>
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              Add
            </Button>
          </Flex>
        </Flex>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={deviceTypes}
          bordered
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
          }}
          onChange={(pagination, filters, sorter) => {
            const field = sorter.field || sortField;
            const order = sorter.order || sortOrder;

            fetchData(
              pagination.current,
              pagination.pageSize,
              search,
              field,
              order
            );
          }}
        />

        <Modal
          title={editData ? "Edit Device Type" : "Add Device Type"}
          centered
          open={isModalOpen}
          footer={false}
          onCancel={handleCancel}
        >
          <DeviceTypeForm editData={editData} saveData={saveData} />
        </Modal>
      </div>
    </Spin>
  );
};

export default DeviceType;
